// Scanning in the laboratory section. A barcode scanner is a keyboard that
// types a whole code within a moment and ends it with Enter, which a person
// cannot do. The station (LabScanStation.js) listens for that on every
// laboratory page, so a scan works without clicking into a field first. A page
// or form that understands some codes itself (its own tube, a machine, a
// material) registers here and is asked before the station looks the code up.
import { createContext, useContext, useEffect, useRef } from 'react';

export const SCAN_MAX_GAP_MS = 60;
export const SCAN_MIN_LENGTH = 4;
// Keys a scanner presses between characters (Shift for capitals) that do not end a code.
const PASSING_KEYS = new Set(['Shift', 'CapsLock', 'Unidentified']);

// Keystrokes in, scans out. `snapshot` is asked for when a burst starts: what
// had the focus and what it held, so the typed characters can be taken back.
export function scanBuffer({ maxGap = SCAN_MAX_GAP_MS, minLength = SCAN_MIN_LENGTH } = {}) {
  let text = '';
  let last = 0;
  let origin = null;
  const reset = () => { text = ''; last = 0; origin = null; };
  return {
    reset,
    key(key, time, snapshot = () => null) {
      // A key that claims to come before the last one is from another clock: never the same burst.
      const together = time >= last && time - last <= maxGap;
      if (key === 'Enter') {
        const scan = text.length >= minLength && together ? { code: text, origin } : null;
        reset();
        return scan;
      }
      if (key.length !== 1) { if (!PASSING_KEYS.has(key)) reset(); return null; }
      if (text === '' || !together) { text = ''; origin = snapshot(); }
      text += key;
      last = time;
      return null;
    },
  };
}

// Codes are compared without regard to case, spaces and punctuation: a label
// reads "GENE-000025", a scanner set to another keyboard may send "gene000025".
export const scanKey = value => String(value ?? '').normalize('NFKC').toUpperCase().replace(/[^0-9A-Z]/g, '');

// What a scan means to one stage: one of its machines by inventory number, or
// a choice of one of its fields by its catalogue number or its own code. A
// supplier's barcode often carries more than the catalogue number, so a scan
// that contains one counts when the number is long enough to be unmistakable.
export function stageScanTarget(stage, code) {
  const key = scanKey(code);
  if (key === '') return null;
  const device = stage.devices.find(item => scanKey(item.inventoryNumber) === key);
  if (device) return { kind: 'device', device };
  let contained = null;
  for (const field of stage.fields) {
    if (field.valueType !== 'option' && field.valueType !== 'options') continue;
    for (const item of field.list.items) {
      if (!item.isActive) continue;
      const catalogue = scanKey(item.catalogueCode);
      if (scanKey(item.code) === key || (catalogue !== '' && catalogue === key)) return { kind: 'item', field, item };
      if (contained === null && catalogue.length >= 6 && key.includes(catalogue)) contained = { kind: 'item', field, item };
    }
  }
  return contained;
}

// What a scanned machine or choice changes in a stage form. Returns the new
// values and machines, or the reason nothing changed: a machine out of service,
// or one that several methods use while none is chosen. A choice that depends
// on another (a kit of a method) brings that other choice with it.
export function applyStageScan(stage, target, values, devices) {
  const next = { ...values };
  const choose = (field, item) => {
    next[field.id] = item;
    for (const child of stage.fields.filter(other => other.parentFieldId === field.id)) delete next[child.id];
  };
  if (target.kind === 'item') {
    const { field, item } = target;
    if (field.parentFieldId !== null && item.parentItemId !== null) {
      const parentField = stage.fields.find(other => other.id === field.parentFieldId);
      const parentItem = parentField ? parentField.list.items.find(other => other.id === item.parentItemId) : null;
      if (parentItem && (!next[parentField.id] || next[parentField.id].id !== parentItem.id)) choose(parentField, parentItem);
    }
    if (field.valueType === 'options') {
      const chosen = next[field.id] || [];
      next[field.id] = chosen.some(other => other.code === item.code) ? chosen : [...chosen, item];
    } else { choose(field, item); }
    return { ok: true, values: next, devices };
  }
  const device = target.device;
  if (device.status !== 'in_service') return { ok: false, reason: 'out_of_service' };
  if (stage.deviceRule === 'per_method') {
    const methodField = stage.fields.find(field => field.role === 'method');
    const current = methodField ? next[methodField.id] : null;
    if (methodField && !(current && current.deviceIds.includes(device.id))) {
      const methods = methodField.list.items.filter(item => item.isActive && item.deviceIds.includes(device.id));
      if (methods.length !== 1) return { ok: false, reason: 'method_first' };
      choose(methodField, methods[0]);
    }
  }
  return { ok: true, values: next,
    devices: stage.deviceRule === 'one_or_more' ? (devices.includes(device.id) ? devices : [...devices, device.id]) : [device.id] };
}

// Two readings and their average: fields named x_1, x_2 and x_avg. Returns the
// average field and its value when both readings are numbers.
export function readingsAverage(fields, values, changed) {
  const match = /^(.+)_[12]$/.exec(changed.code);
  if (!match) return null;
  const find = suffix => fields.find(field => field.code === `${match[1]}_${suffix}` && field.valueType === 'number');
  const first = find('1');
  const second = find('2');
  const average = find('avg');
  if (!first || !second || !average) return null;
  const numbers = [values[first.id], values[second.id]].map(value => (value === '' || value === undefined || value === null ? NaN : Number(value)));
  if (numbers.some(number => !Number.isFinite(number))) return { field: average, value: '' };
  return { field: average, value: String(Number(((numbers[0] + numbers[1]) / 2).toFixed(4))) };
}

export const LabScanContext = createContext(null);

// Registers what a page or form does with scans, for as long as it is on
// screen. `entry` may hold:
//   match(code, kind): answers at once with a function to run when the code is
//     its own, or null. `kind` is 'text' when the scan was typed into a text field.
//   tubeCode + onTube(): the tube this form is for; a scan of it confirms the form.
//   dirty: unsaved entries for that tube, so a scan of another tube asks first.
// Returns `touch`, which the form calls when it is being worked in, so a
// machine or material scan goes to it and not to a form beside it.
export function useLabScan(entry) {
  const station = useContext(LabScanContext);
  const current = useRef(entry);
  current.current = entry;
  const handle = useRef(null);
  useEffect(() => {
    if (!station) return undefined;
    handle.current = station.register(current);
    return () => { handle.current.remove(); handle.current = null; };
  }, [station]);
  return { station, touch: () => { if (handle.current) handle.current.touch(); } };
}
